'use client'

import type { Schema } from 'yup'
import type { AppendFieldRulesType } from './_types/UseSchemaTypes'
import type { ValidationMessageType } from './_helpers/constructStandardValidationMessage'
import { generateSchema } from './_helpers/generateSchema'
import { useEffect, useState } from 'react'
import { useFormElement } from '../utility-hooks/UseFormElement'
import { ReplacerObject } from '../../../Dictionary/DictionaryTypes'
import { assert } from '../../../Helpers/logging'

export interface UseSchemaOptions {
    excludeFieldNames?: string[]
    appendFieldRules?: AppendFieldRulesType
    customSchema?: Schema | null
}

interface UseSchemaReturnType {
    schema: Schema | null
    regenerateSchema: () => void
}

/**
 * @description
 * constructs a validation message object that can be passed to a yup NumberSchema | TextSchema | etc.
 * */
export const validationMessage = (
    fieldConstraint: string,
    messageKey: string,
    replacer?: ReplacerObject,
): ValidationMessageType => {
    return { [fieldConstraint]: { textKey: messageKey, replacer } }
}

/**
 * @namespace
 * @description useSchema generates a schema based on an actual HTML form element.
 * Through the options parameter, input fields can be excluded from the schema, or
 * the schema of fields can be expanded with extra fieldRules
 *
 * @param options.excludeFieldNames -- excludes fields from the schema. This is useful when you
 * have an optional field for which you want to provide hints to the user when the input is invalid.
 * @param options.appendFieldRules --  add extra validation rules to one or more input fields. Can
 * also be used to replace the validation rules of a field.
 * @param options.customSchema -- providing a custom schema skips all logic of this hook. this is
 * useful when useSchema is used inside another hook, where a custom schema is provided. When
 * using  useSchema standalone, choose to not use useSchema instead of providing a custom schema.
 * null can also be provided to options.customSchema. This can be useful for debugging purposes.
 *
 * @returns object.schema -- yup schema that was generated
 * @returns object.regenerateSchema -- if there was a change in the composition of your form because of
 * a user action, you can use this function to regenerate the schema. The schema will be regenerated
 * two event loop frames after this function is called, awaiting the correct state of the form.
 *
 * @example minimal
 * ```
 * const { schema } = useSchema('my-form')
 * ```
 *
 * @example append field rules
 * ```
 * const appendFieldRules = {['my-number-field']: (numberSchema: NumberSchema) => numberSchema.integer()}
 * const { schema } = useSchema('my-form', {appendFieldRules})
 * ```
 *
 * @example replace field rules
 * ```
 * const appendFieldRules = {['my-number-field']: (_: NumberSchema) => number().min().integer()}
 * const { schema } = useSchema('my-form', {appendFieldRules})
 * ```
 *
 * @example exclude fields
 * ```
 * const { schema } = useSchema('my-form', {excludeFieldNames: ['zip-code-field']})
 * ```
 * */
export const useSchema = (formId: string, options?: UseSchemaOptions): UseSchemaReturnType => {
    const [schema, setSchema] = useState<Schema | null>(options?.customSchema ?? null)
    const formElement = useFormElement(formId)
    useEffect(() => {
        if (options?.customSchema === null) {
            setSchema(null)
            return
        }
        if (options?.customSchema) return
        if (!formElement) return

        setSchema(generateSchema(formElement, options?.appendFieldRules, options?.excludeFieldNames))
    }, [formElement])

    const regenerateSchema = () => {
        assert(!!formElement, 'There probably is no need to regenerate the schema.')
        if (!formElement) {
            return
        }

        requestAnimationFrame(() => {
            // render loop 1: in this render loop the form will probably be built

            requestAnimationFrame(() => {
                // render loop 2: in this render loop the form is in it's desired state
                // a schema can now be generated
                setSchema(generateSchema(formElement, options?.appendFieldRules, options?.excludeFieldNames))
            })
        })
    }

    return { schema, regenerateSchema }
}
